import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "../TrajetClient/HomePage.css";
import "./HomePage.css";
import BookingApi from "Services/Booking";
import passengerApi from "Services/Passenger";
import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";

const historyTabs = [
  { label: "Tous", icon: "all", active: true, value: "all" },
  { label: "Covoiturage", icon: "car", value: "covoiturage" },
  { label: "Bus", icon: "bus", value: "bus" },
  { label: "Train", icon: "train", value: "train" },
  { label: "Livraison", icon: "box", value: "livraison" },
  { label: "Courses", icon: "bag", value: "course" },
];

const statusOptions = [
  { label: "Tous les statuts", value: "all" },
  { label: "Draft", value: "draft" },
  { label: "Published", value: "published" },
  { label: "Full", value: "full" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Completed", value: "completed" },
];

const statusLabelMap = {
  draft: "Draft",
  published: "Published",
  full: "Full",
  cancelled: "Cancelled",
  completed: "Completed",
};

const statusToneMap = {
  draft: "is-active",
  published: "is-active",
  full: "is-past",
  cancelled: "is-past",
  completed: "is-past",
};

const zoneForDate = (timezone) => timezone || "Africa/Lagos";

const getDateParts = (value, timezone) => {
  const date = value ? moment(value).tz(zoneForDate(timezone)) : null;
  return {
    date: date ? date.format("DD MMM") : "--",
    weekday: date ? date.format("dddd") : "--",
    time: date ? date.format("HH:mm") : "--:--",
    full: date ? date.format("DD MMM YYYY HH:mm") : "--",
  };
};

const toMoney = (value, currency = "XAF") => {
  if (value === null || value === undefined || value === "") return `-- ${currency}`;
  const amount = Number(value);
  if (Number.isNaN(amount)) return `${value} ${currency}`;
  return `${new Intl.NumberFormat("fr-FR").format(amount)} ${currency}`;
};

const formatEnumLabel = (value) => {
  if (!value) return "--";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/(^|\s)\S/g, (match) => match.toUpperCase());
};

const formatGridDate = (value, timezone) => {
  if (!value) return "--";

  const date = moment(value).tz(zoneForDate(timezone));
  return date.isValid() ? date.format("DD MMM YYYY HH:mm") : "--";
};

const safeJoin = (...parts) => parts.filter(Boolean).join(" ");

const normalizeBooking = (booking, index) => {
  const ride = booking?.ride || {};
  const passenger = booking?.passenger || {};
  const origin = ride?.places_rides_origin_place_idToplaces || {};
  const destination = ride?.places_rides_destination_place_idToplaces || {};
  const paymentMode = formatEnumLabel(
    booking?.payment_mode || booking?.paymentMode || ride?.payment_mode || ride?.paymentMode || "--"
  );
  const status = String(booking?.status || "").toLowerCase();

  return {
    id: booking?.id || ride?.id || `booking-${index}`,
    bookingId: booking?.id || "-",
    rideId: ride?.id || "-",
    passengerName: safeJoin(passenger?.first_name, passenger?.last_name) || "Passager",
    phone: passenger?.phone || "-",
    rideType: String(ride?.ride_type || "").toLowerCase(),
    bookingStatus: status,
    rideStatus: String(ride?.status || "").toLowerCase(),
    departureAt: ride?.departure_at || null,
    arrivalAt: ride?.estimated_arrival_at || null,
    duration: ride?.estimated_duration || "--",
    totalAmount: booking?.total_amount,
    serviceFee: booking?.service_fee,
    currency: booking?.currency || ride?.currency || "XAF",
    pickupPlace: origin?.name || "Depart non precise",
    dropLocation: destination?.name || "Arrivee non precisee",
    requestDetails: safeJoin(passenger?.first_name, passenger?.last_name) || "Passager",
    paymentMode,
    totalDisplay: toMoney(booking?.total_amount, booking?.currency || ride?.currency || "XAF"),
    commissionDisplay: toMoney(booking?.service_fee, booking?.currency || ride?.currency || "XAF"),
    statusLabel: statusLabelMap[status] || formatEnumLabel(booking?.status || booking?.bookingStatus || "Trajet"),
    statusTone: statusToneMap[status] || "is-active",
    seatsBooked: booking?.seats_booked ?? 0,
    totalSeats: ride?.total_seats ?? null,
    availableSeats: ride?.available_seats ?? null,
    originName: origin?.name || "Depart non precise",
    originPlace: origin?.addressLocality || origin?.addressCountry || "-",
    destinationName: destination?.name || "Arrivee non precisee",
    destinationPlace: destination?.addressLocality || destination?.addressCountry || "-",
    cancellationReason: booking?.cancellation_reason || "",
    createdAt: booking?.created_at || null,
    confirmedAt: booking?.confirmed_at || null,
  };
};

const normalizeList = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((item, index) => normalizeBooking(item, index));
};

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
      <path d="M8 2.8v4M16 2.8v4M3 9h18" />
    </svg>
  );
}

function ChevronDownIcon(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function TripIcon({ kind }) {
  if (kind === "all") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 13.5 6.6 9.2A2.4 2.4 0 0 1 8.9 7.5h6.2a2.4 2.4 0 0 1 2.3 1.7L19 13.5" />
        <rect x="4" y="13.5" width="16" height="4.5" rx="1.5" />
        <circle cx="8" cy="18.5" r="1.2" />
        <circle cx="16" cy="18.5" r="1.2" />
      </svg>
    );
  }

  if (kind === "car") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 13.5 6.6 9.2A2.4 2.4 0 0 1 8.9 7.5h6.2a2.4 2.4 0 0 1 2.3 1.7L19 13.5" />
        <rect x="4" y="13.5" width="16" height="4.5" rx="1.5" />
        <circle cx="8" cy="18.5" r="1.2" />
        <circle cx="16" cy="18.5" r="1.2" />
      </svg>
    );
  }

  if (kind === "bus") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="5" y="5" width="14" height="13" rx="3" />
        <path d="M8 9h8M8 12h8M9 18v1M15 18v1" />
      </svg>
    );
  }

  if (kind === "train") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 4h10a3 3 0 0 1 3 3v7a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V7a3 3 0 0 1 3-3Z" />
        <path d="M8 9h8M8 12h8M9 18v1M15 18v1" />
      </svg>
    );
  }

  if (kind === "box") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="m4.5 8 7.5-3.5L19.5 8 12 11.5 4.5 8Z" />
        <path d="M4.5 8v8l7.5 3.5 7.5-3.5V8" />
        <path d="M12 11.5v8" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 8h10l1 11H6L7 8Z" />
      <path d="M9 8a3 3 0 0 1 6 0" />
      <path d="M10 11h4" />
    </svg>
  );
}

function TripTypeTab({ tab, active, onClick }) {
  return (
    <button type="button" className={`tc-trip-tab ${active ? "is-active" : ""}`} onClick={onClick}>
      <span className="tc-trip-tab-icon">
        <TripIcon kind={tab.icon} />
      </span>
      <span>{tab.label}</span>
    </button>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="hc-detail-row">
      <span className="hc-detail-label">{label}</span>
      <span className="hc-detail-value">{value || "--"}</span>
    </div>
  );
}

function HistoryRow({ trip, onOpenDetails, onOpenTicket, isTicketLoading }) {
  const departure = getDateParts(trip?.departureAt);
  const arrival = getDateParts(trip?.arrivalAt);
  const bookingTone = statusToneMap[trip?.bookingStatus] || "is-active";
  const bookingLabel = statusLabelMap[trip?.bookingStatus] || trip?.bookingStatus || "Unknown";

  return (
    <article className="tc-trip-row">
      <div className="tc-trip-date">
        <strong>{departure.date}</strong>
        <span>{departure.weekday}</span>
      </div>

      <div className="tc-trip-times">
        <div className="tc-trip-time-start">{departure.time}</div>
        <div className="tc-trip-rail" aria-hidden="true">
          <span />
        </div>
        <div className="tc-trip-time-end">{arrival.time}</div>
      </div>

      <div className="tc-trip-route">
        <div className="tc-trip-stop">
          <strong>{trip?.originName}</strong>
          <span>{trip?.originPlace}</span>
        </div>
        <div className="tc-trip-stop tc-trip-stop-end">
          <strong>{trip?.destinationName}</strong>
          <span>{trip?.destinationPlace}</span>
        </div>
      </div>

      <div className="tc-trip-mode">
        <span className="tc-trip-mode-icon" aria-hidden="true">
          <TripIcon kind={trip?.rideType || "car"} />
        </span>
        <span>{trip?.rideType || "covoiturage"}</span>
      </div>

      <div className="tc-trip-meta">
        <div className="tc-trip-duration">
          <strong>{trip?.duration || "--"}</strong>
          <span>
            {trip?.seatsBooked}/{trip?.totalSeats ?? "--"} sieges
          </span>
        </div>
      </div>

      <div className="tc-trip-passenger">
        <span aria-hidden="true">👤</span>
        <span>{trip?.passengerName}</span>
      </div>

      <div className="tc-trip-price">
        <strong>{toMoney(trip?.totalAmount, trip?.currency)}</strong>
        <span className={bookingTone}>{bookingLabel}</span>
      </div>

      <button type="button" className="tc-trip-detail-btn" onClick={() => onOpenDetails(trip)}>
        Voir le detail
      </button>

      <button
        type="button"
        className="tc-trip-ticket-btn"
        onClick={() => onOpenTicket(trip)}
        disabled={isTicketLoading}
      >
        {isTicketLoading ? "Chargement..." : "Acceder au ticket"}
      </button>

      <button type="button" className="tc-trip-more-btn" aria-label="Plus d'options" onClick={() => onOpenDetails(trip)}>
        <span />
        <span />
        <span />
      </button>
    </article>
  );
}

function Pagination({ total, visible }) {
  return (
    <div className="tc-pagination">
      <span className="tc-pagination-label">
        1 - {visible} sur {total} trajets
      </span>
      <div className="tc-pagination-controls">
        <button type="button" aria-label="Page precedente" className="tc-page-nav">
          <span>‹</span>
        </button>
        <button type="button" className="tc-page is-active">
          1
        </button>
        <button type="button" aria-label="Page suivante" className="tc-page-nav">
          <span>›</span>
        </button>
      </div>
    </div>
  );
}

function HistoryDetailsDialog({ open, trip, timezone, onClose }) {
  if (!trip) return null;

  const departure = getDateParts(trip?.departureAt, timezone);
  const arrival = getDateParts(trip?.arrivalAt, timezone);
  const confirmed = trip?.confirmedAt ? moment(trip.confirmedAt).tz(zoneForDate(timezone)).format("DD MMM YYYY HH:mm") : "--";
  const bookingStatusLabel = trip?.statusLabel || statusLabelMap[trip?.bookingStatus] || trip?.bookingStatus || "--";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <div>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Details du trajet
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {trip?.originName} - {trip?.destinationName}
            </Typography>
          </div>
          <Chip
            label={bookingStatusLabel}
            color={trip?.bookingStatus === "cancelled" ? "error" : trip?.bookingStatus === "completed" ? "success" : "primary"}
            variant="outlined"
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <div className="hc-detail-card">
            <DetailRow label="Passager" value={trip?.passengerName} />
            <DetailRow label="Telephone" value={trip?.phone} />
            <DetailRow label="Type" value={trip?.rideType} />
            <DetailRow label="Montant" value={toMoney(trip?.totalAmount, trip?.currency)} />
          </div>
          <Divider />
          <div className="hc-detail-grid">
            <div className="hc-detail-card">
              <DetailRow label="Statut booking" value={trip?.bookingStatus} />
              <DetailRow label="Cree le" value={`${departure.date} ${departure.weekday} ${departure.time}`} />
              <DetailRow label="Confirme le" value={confirmed} />
            </div>
            <div className="hc-detail-card">
              <DetailRow label="Statut ride" value={trip?.rideStatus} />
              <DetailRow label="Depart" value={trip?.originName} />
              <DetailRow label="Destination" value={trip?.destinationName} />
              <DetailRow label="Arrivee estimee" value={`${arrival.date} ${arrival.weekday} ${arrival.time}`} />
            </div>
          </div>
          <DetailRow label="Motif d'annulation" value={trip?.cancellationReason || "--"} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function HistoryContent({ trips, timezone, onOpenDetails, onOpenTicket, ticketLoadingId }) {
  if (!trips.length) {
    return null;
  }

  return (
    <section className="tc-trip-section">
      <div className="tc-trip-list">
        {trips.map((trip) => (
          <HistoryRow
            key={trip.id}
            trip={trip}
            onOpenDetails={onOpenDetails}
            onOpenTicket={onOpenTicket}
            isTicketLoading={ticketLoadingId === trip.id}
          />
        ))}
      </div>
    </section>
  );
}

const HistoryTripsTable = ({ rows, timezone, onOpenDetails, onOpenTicket, ticketLoadingId, loading }) => {
  const [pageSize, setPageSize] = useState(5);

  const renderTextCell = useCallback(
    (value) => (
      <Typography variant="body2" noWrap title={value ?? "--"} sx={{ width: "100%" }}>
        {value ?? "--"}
      </Typography>
    ),
    []
  );

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.NO",
        minWidth: 70,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => renderTextCell(params?.row?.sno),
      },

      {
        field: "pickupPlace",
        headerName: "PICKED UP",
        minWidth: 180,
        flex: 1.2,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.pickupPlace),
      },
      {
        field: "dropLocation",
        headerName: "DROP LOCATION",
        minWidth: 180,
        flex: 1.2,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.dropLocation),
      },


      {
        field: "datedOn",
        headerName: "DATED ON",
        minWidth: 150,
        flex: 1,
        sortable: true,
        renderCell: (params) =>
          renderTextCell(params?.row?.datedOn || formatGridDate(params?.row?.departureAt || params?.row?.createdAt, timezone)),
      },
      {
        field: "status",
        headerName: "STATUS",
        minWidth: 150,
        flex: 1,
        sortable: true,
        renderCell: (params) => (
          <span className={`tc-status-pill tc-status-pill--${params?.row?.statusTone || "is-active"}`}>
            {params?.row?.statusLabel || "--"}
          </span>
        ),
      },
      {
        field: "paymentMode",
        headerName: "PAYMENT MODE",
        minWidth: 150,
        flex: 1,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.paymentMode),
      },
      {
        field: "totalDisplay",
        headerName: "TOTAL",
        minWidth: 120,
        flex: 0.9,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.totalDisplay),
      },
      {
        field: "commissionDisplay",
        headerName: "COMMISSION",
        minWidth: 140,
        flex: 0.9,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.commissionDisplay),
      },
      {
        field: "ticket",
        headerName: "TICKET",
        minWidth: 160,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Button
            type="button"
            variant="outlined"
            size="small"
            disabled={ticketLoadingId === params?.row?.id}
            onClick={() => onOpenTicket(params?.row)}
            sx={{
              minWidth: 122,
              height: 34,
              px: 1.75,
              borderRadius: "10px",
              borderColor: "#ff9800",
              background: "#ffffff",
              color: "#ff9800",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              boxShadow: "none",
              lineHeight: 1,
              "&:hover": {
                borderColor: "#ef8b00",
                background: "#fff8ef",
              },
              "&.Mui-disabled": {
                borderColor: "rgba(255, 152, 0, 0.35)",
                color: "rgba(255, 152, 0, 0.5)",
                background: "#ffffff",
                boxShadow: "none",
              },
            }}
          >
            {ticketLoadingId === params?.row?.id ? "Chargement..." : "Acceder au ticket"}
          </Button>
        ),
      },
    ],
    [onOpenDetails, onOpenTicket, renderTextCell, ticketLoadingId, timezone]
  );

  return (
    <Paper
      elevation={0}
      className="tc-table-card"
      sx={{
        borderRadius: 4,
        border: "1px solid rgba(15, 39, 72, 0.08)",
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        loading={loading}
        disableSelectionOnClick
        disableColumnMenu
        density="compact"
        pagination
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 25, 50]}
        rowHeight={60}
        headerHeight={56}
        getRowId={(row) => row.id}
        localeText={{ noRowsLabel: "No results found." }}
        className="tc-data-grid-table"
      />
    </Paper>
  );
};

export default function HistoriqueClient() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timezone = useSelector((state) => state?.timezone?.timezone);
  const passengerId = useSelector((state) => state?.AuthReducer?.userid || localStorage.getItem("userid") || "");

  const [selectedRideType, setSelectedRideType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [ticketLoadingId, setTicketLoadingId] = useState("");

  const loadHistory = useCallback(async () => {
    if (!passengerId) {
      setBookings([]);
      setTotal(0);
      setLoadError("Impossible de charger l'historique sans identifiant utilisateur.");
      return;
    }

    setIsLoading(true);
    setLoadError("");
    dispatch(showLoader("Chargement de l'historique..."));

    try {
      const params = { id: passengerId };
      if (selectedRideType !== "all") params.ride_type = selectedRideType;
      if (selectedStatus !== "all") params.status = selectedStatus;
      if (selectedDate) params.date = selectedDate;

      const res = await passengerApi.getPassengerRidesList(params);
      if (res?.data?.status === true) {
        const list = normalizeList(res?.data?.data?.bookings);
        setBookings(list);
        setTotal(Number(res?.data?.data?.total ?? list.length ?? 0));
      } else {
        setBookings([]);
        setTotal(0);
        setLoadError(res?.data?.message || "Aucun trajet trouve.");
      }
    } catch (error) {
      setBookings([]);
      setTotal(0);
      const message = error?.response?.data?.message || "Erreur lors du chargement de l'historique.";
      setLoadError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setIsLoading(false);
      dispatch(hideLoader());
    }
  }, [dispatch, passengerId, selectedDate, selectedRideType, selectedStatus]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const filteredBookings = useMemo(() => bookings, [bookings]);
  const tableRows = useMemo(() => {
    return [...filteredBookings]
      .sort((a, b) => {
        const aTime = moment(a?.departureAt || a?.createdAt || 0).valueOf();
        const bTime = moment(b?.departureAt || b?.createdAt || 0).valueOf();
        return bTime - aTime;
      })
      .map((trip, index) => ({
        ...trip,
        sno: index + 1,
        datedOn: formatGridDate(trip?.departureAt || trip?.createdAt, timezone),
      }));
  }, [filteredBookings, timezone]);

  const onOpenDetails = useCallback((trip) => {
    setSelectedTrip(trip);
    setDetailsOpen(true);
  }, []);

  const onCloseDetails = useCallback(() => {
    setDetailsOpen(false);
  }, []);

  const onOpenTicket = useCallback(
    async (trip) => {
      const bookingId = trip?.bookingId;

      if (!bookingId || bookingId === "-") {
        dispatch(
          showSnackbar({
            message: "Impossible d'ouvrir le ticket pour ce booking.",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
        return;
      }

      setTicketLoadingId(trip.id);
      dispatch(showLoader("Chargement du ticket de réservation..."));

      try {
        const response = await BookingApi.getBookingById(bookingId);
        const bookingData = response?.data?.data ?? response?.data ?? null;

        if (!bookingData) {
          throw new Error("Booking introuvable.");
        }

        const rideData =
          bookingData?.ride ||
          bookingData?.rides ||
          bookingData?.rides?.[0] ||
          trip?.ride ||
          trip?.raw?.ride ||
          trip?.raw?.rides ||
          trip;

        const passengerData = bookingData?.passenger || bookingData?.user || bookingData?.users || null;
        const passengerCount = Number(bookingData?.seats_booked ?? trip?.seatsBooked ?? 1);
        const passengerEmail =
          passengerData?.email ||
          bookingData?.email ||
          bookingData?.user_email ||
          localStorage.getItem("email") ||
          "";

        const payload = {
          booking: bookingData,
          ride: rideData,
          passengerCount,
          passengerEmail,
        };

        sessionStorage.setItem("bookingConfirmationPayload", JSON.stringify(payload));
        navigate("/booking-confirmation", { state: payload });
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || "Impossible de charger le ticket.";
        dispatch(
          showSnackbar({
            message,
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      } finally {
        setTicketLoadingId("");
        dispatch(hideLoader());
      }
    },
    [dispatch, navigate]
  );

  const lastUpdated = moment().tz(zoneForDate(timezone)).format("DD/MM/YYYY HH:mm");
  void total;
  void Pagination;
  void HistoryContent;

  return (
    <>
      <div className="tc-hero">
        <div>
          <h1>Historique</h1>
          <p>Retrouvez tous vos trajets passés.</p>
        </div>
      </div>

      <div className="hc-toolbar">
        <div className="tc-trip-tabs">
          {historyTabs.map((tab) => (
            <TripTypeTab
              key={tab.label}
              tab={tab}
              active={selectedRideType === tab.value}
              onClick={() => setSelectedRideType(tab.value)}
            />
          ))}
        </div>

        <div className="hc-toolbar-right">
          <button type="button" className="hc-date-filter" onClick={(event) => setDateAnchorEl(event.currentTarget)}>
            <CalendarIcon />
            <span>{selectedDate ? selectedDate : "Filtrer par date"}</span>
            <ChevronDownIcon className="hc-date-chevron" />
          </button>
        </div>
      </div>

      <Menu
        anchorEl={dateAnchorEl}
        open={Boolean(dateAnchorEl)}
        onClose={() => setDateAnchorEl(null)}
        keepMounted
      >
        <div style={{ padding: 12, minWidth: 260 }}>
          <TextField
            type="date"
            fullWidth
            label="Date"
            InputLabelProps={{ shrink: true }}
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
          <div style={{ height: 12 }} />
            <TextField
              select
              fullWidth
              label="Statut"
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          <div style={{ height: 12 }} />
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              setSelectedDate("");
              setSelectedStatus("all");
            }}
          >
            Reinitialiser
          </Button>
        </div>
      </Menu>

      {loadError ? <Alert severity="error" sx={{ mt: 2 }}>{loadError}</Alert> : null}

      <HistoryTripsTable
        rows={tableRows}
        timezone={timezone}
        onOpenDetails={onOpenDetails}
        onOpenTicket={onOpenTicket}
        ticketLoadingId={ticketLoadingId}
        loading={isLoading}
      />

      <div style={{ marginTop: 12, color: "#6a778a", fontSize: 12, fontWeight: 600 }}>
        Derniere mise a jour: {lastUpdated}
      </div>

      <HistoryDetailsDialog open={detailsOpen} trip={selectedTrip} timezone={timezone} onClose={onCloseDetails} />
    </>
  );
}
